import axios, {
  AxiosRequestConfig,
  CancelToken,
  CancelTokenSource,
} from "axios";
import { get } from "lodash";
import { AgentPersonalDetailRequest } from "@models/AgentPersonalDetailRequest.model";
import { EzSaleRequest } from "@models/EzSaleRequest.model";
import { QASPostalCodeRequest } from "@models/QASPostalCodeRequest";
import {
  MyInfoUrlRequest,
  AddMyInfoRequest,
  IMyInfoQuestionRequest,
} from "@models/MyInfoRequest.model";
import { PaymentRequest } from "@models/PaymentRequest.model";
import { BMICheckRequest } from "@models/BMIRequest.model";
import { ContactRequest } from "@models/ContactRequest.model";
import { ROUTE_PATH } from "@static/route-paths";
import generateSqsQuotationRequest from "@pages/ShieldStarter/Helpers/generateSqsQuotationRequest";
import { SHIELD_STARTER_HELTH_PLUS } from "@static/staticConstents";
import {
  IEvergreenPaymentPayload,
  IFastBankUrlPayload,
  IFastPaymentPayload,
  ISaveInfoPayload,
} from "@utils/interfaces";
import { ContactInfo } from "@redux/slices/EVER_GREEN/contactSlice";

export const EVERGREEN_API = {
  MY_INFO_URL: "/ever-green/myinfo/url",
  ADD_MANUAL_DATA: "/ever-green/add-myinfo-manual-detail",
  ADDRESS: "/ever-green/address",
  CONTACT_US: "/ever-green/add-drop-off-journey",
};

export const CCP_API_SINGPASS = {
  MY_INFO_URL: "/ccp/myinfo/url",
};

export const getBaseURL = () => {
  if (process.env.REACT_APP_ENVIRONMENT === "DEV") {
    const host =
    window.location.protocol +
    "//" +
    window.location.host +
    window.location.pathname.replace("index.html", "").slice(0, -1);

    return host;
  }

  const configURLInHtml = get(window, "AVIVA_VARIABLE_CONFIG.urlApi", null);
  const host =
    window.location.protocol +
    "//" +
    window.location.host +
    window.location.pathname.replace("index.html", "").slice(0, -1); //'/api/v1';
  
  return configURLInHtml ? configURLInHtml : host;
};

const config: AxiosRequestConfig = {
  baseURL: getBaseURL(),
  headers: {
    "Access-Control-Allow-Origin": "*",
    "Content-Type": "application/json",
    "X-XSRF-TOKEN": window.sessionStorage.getItem("token") || "",
  },
};

export const protocol = axios.create(config);

// Product code ENUM for reusing the same function for different products
export enum PRODUCT_CODE_API {
  EVER_GREEN = "ever-green",
  SHIELD_STARTER = "shield-starter",
  CANCER_COVER = "cancer-cover",
}

const ADD_MYINFO_URL = "/myInfo";
export const postSavingMyInfo = (
  payload: AddMyInfoRequest | IMyInfoQuestionRequest
) => {
  console.debug("postSavingMyInfo/payload:", JSON.stringify(payload, null, 2));
  return protocol.post(ADD_MYINFO_URL, payload);
};

const OCCUPATIONS_API = "/occupation";
export const getOccupations = (
  category: string,
  productCode?: PRODUCT_CODE_API
) => {
  if (productCode) {
    switch (productCode) {
      case PRODUCT_CODE_API.EVER_GREEN: {
        return protocolNew.get(`/ever-green/occupation/list`);
      }
    }
  }
  return protocol.get(`${OCCUPATIONS_API}/${category}`);
};

const RENTALPROPERTY_API = "/rentalProperty";
export const getRentalProperty = (postalcode: string) => {
  return protocol.get(`${RENTALPROPERTY_API}/${postalcode}`);
};

const SENDMAIL_API = "/sendMail";
export const sendMail = (
  referenceNo: string,
  productId: any,
  customerId: any
) => {
  return protocol.get(
    `${SENDMAIL_API}/${referenceNo}/${productId}/${customerId}`
  );
};

const SENDMAIL_MLTC_API = "/sendMailMltc";
export const sendMailMltc = (
  referenceNo: string,
  productId: any,
  customerId: any
) => {
  console.debug(
    "sendMailMltc/payload: GET",
    `${SENDMAIL_MLTC_API}/${referenceNo}/${productId}/${customerId}`,
    null,
    2
  );
  return protocol.get(
    `${SENDMAIL_MLTC_API}/${referenceNo}/${productId}/${customerId}`
  );
};

const POLICYNO_API = "/policyNumber";
export const getPolicyNo = (
  referenceNo: string,
  productId: any,
  customerId: any
) => {
  return protocol.get(
    `${POLICYNO_API}/${referenceNo}/${productId}/${customerId}`
  );
};

const CLIENT_DETAIL_CHECK_API = "/clientDetailCheck";
export const clientDetailCheck = (
  nricNo: string,
  nationality: string,
  gender: string,
  birthDate: string
) => {
  return protocol.post(CLIENT_DETAIL_CHECK_API, {
    nricNo,
    nationality,
    gender,
    birthDate,
  });
};

const EXISTING_LIFE_HEALTH_API = "/existingLifeHealthPolicyCheck";
export const existingLifeHealthCheck = (nricNo: string) => {
  return protocol.post(EXISTING_LIFE_HEALTH_API, { nricNo });
};

const COMPUTE_PREMIUM_API = "/computePremium";
const COMPUTE_PREMIUM_MLTC_API = "/computePremiumMltc";
export const postComputePremium = (payload: any, mltc: boolean = false) => {
  //SQSPocketRequest
  console.debug(
    "QnB ➤ postComputePremium(payload) ➤",
    JSON.stringify(payload, null, 2)
  );
  return protocol.post(
    mltc ? COMPUTE_PREMIUM_MLTC_API : COMPUTE_PREMIUM_API,
    payload
  );
};

const DECLARATION_PDF_API = "/qnbIllustration";
const DECLARATION_PDF_MLTC_API = "/qnbIllustrationMltc";
export const postDeclarationPDF = (payload: any, mltc: boolean = false) => {
  console.debug(
    `QnB ➤ postDeclarationPDF(payload, mltc == ${mltc}) ➤`,
    JSON.stringify(payload, null, 2)
  );
  return protocol.post(
    mltc ? DECLARATION_PDF_MLTC_API : DECLARATION_PDF_API,
    payload
  );
};

const PAYMENT_API = "/paymentURL";
export const postPayment = (payload: PaymentRequest) => {
  console.debug("postPayment/payload:", JSON.stringify(payload, null, 2));
  return protocol.post(PAYMENT_API, payload);
};

const SAVE_PAYMENT_API = "/payment";
export const savePayment = (payload: any) => {
  console.debug(
    "QnB ➤ savePayment(payload) ➤",
    JSON.stringify(payload, null, 2)
  );
  return protocol.post(SAVE_PAYMENT_API, payload);
};

const SERVICING_AGENT_INFO_API = "/getPersonalDetail";
export const postGetPersonalDetails = (payload: AgentPersonalDetailRequest) => {
  console.debug(
    `QnB ➤ postGetPersonalDetails(payload) ➤`,
    JSON.stringify(payload, null, 2)
  );
  return protocol.post(SERVICING_AGENT_INFO_API, payload);
};

const AGENT_INFO_API = "/retrieveInformation";
export const postRetrieveInformation = (payload: any) => {
  console.debug(
    `QnB ➤ postRetrieveInformation(payload) ➤`,
    JSON.stringify(payload, null, 2)
  );
  return protocol.post(AGENT_INFO_API, payload);
};

const GET_CAMPIAGN_API = "/shield-starter/campaign";
export const postCampaign = (payload: any) => {
  console.debug(
    `QnB ➤ postCampaign(payload) ➤`,
    JSON.stringify(payload, null, 2)
  );
  return protocol.post(GET_CAMPIAGN_API, payload);
};

const SINGPASS_URL_API = "myinfo/url";
export const getAuthoriseURL = (productCode: string) => {
  const url = window.location.href.replace("/./", "/");
  const [origin, params] = url.split("?");
  const searchParams = new URLSearchParams(params);
  // searchParams.delete('status');
  // searchParams.delete('transaction_id');
  const campaignCode = searchParams.get("cc");
  //const callback = `${origin}?${searchParams.toString()}`;// + '&rental=true&clientDetail=true&policy=true';
  let payload: any = new MyInfoUrlRequest(productCode, campaignCode);

  if (productCode === "mltc") {
    payload = { ...payload, productType: "mltc" };
  }

  return protocol.post(SINGPASS_URL_API, payload);
};

const GET_PERSON_API = "/myinfo/person";
export const getPerson = (transaction_id: string, productCode: string = "") => {
  const app_callback = window.location.href.replace("/./", "/");
  let payload: any = { transaction_id, app_callback };

  if (productCode === "mltc") {
    payload = { ...payload, productType: "mltc" };
  }

  return protocol.post(GET_PERSON_API, payload);
};

const VALIDATION_MAX_MIN_ASSURED_AND_FLAG_API = "/calMinSumAssured";
export const postCalculateMinAssuredAndFlag = (payload: any) => {
  console.debug(
    "postCalculateMinAssuredAndFlag/payload:",
    JSON.stringify(payload, null, 2)
  );
  return protocol.post(VALIDATION_MAX_MIN_ASSURED_AND_FLAG_API, payload);
};

const VALIDATE_MAX_ASSURED_AND_FLAG_API = "/isSubstantialRecordAvailable";
export const postValidateMaxSumAssuredAndFlag = (payload: any) => {
  return protocol.post(VALIDATE_MAX_ASSURED_AND_FLAG_API, payload);
};

const CHECK_ELIGIBILITY_FOR_CAMPAIGN_API = "/checkEligibilityForCampaign";
export const postCheckEligibityForCampaign = (payload: EzSaleRequest) => {
  return protocol.post(CHECK_ELIGIBILITY_FOR_CAMPAIGN_API, payload);
};

const QAS_POSTAL_CODE_RETRIVEVAL_ADDRESS_API = "/address";
export const postQASPostalAddress = (payload: QASPostalCodeRequest) => {
  return protocol.post(QAS_POSTAL_CODE_RETRIVEVAL_ADDRESS_API, payload);
};

const BMI_CHECK_API = "/bmi";
export const postBMICheck = (payload: BMICheckRequest) => {
  return protocol.post(BMI_CHECK_API, payload);
};

const PRODUCT_CHECK_API = "/productCheckMltc";
export const postProductCheckMltc = (nric: string) => {
  return protocol.post(PRODUCT_CHECK_API, { product: "MLTC", nricNo: nric });
};

const CONTACT_API = "/contactForm";
export const postContact = (payload: ContactRequest) => {
  return protocol.post(CONTACT_API, payload);
};

const CONTACT_MLTC_API = "/contactFormMltc";
export const postContactMltc = (payload: any) => {
  return protocol.post(CONTACT_MLTC_API, payload);
};

const CONTACT_CIMB_MLTC_API = "/sendEmailToCIMBMltc";
export const postContactCIMBMltc = (payload: any) => {
  console.debug(
    "postContactCIMBMltc/payload:",
    JSON.stringify(payload, null, 2)
  );
  return protocol.post(CONTACT_CIMB_MLTC_API, payload);
};

export const postContactEvergreen = (payload: ContactInfo) => {
  console.debug(
    "postContactEvergreen/payload:",
    JSON.stringify(payload, null, 2)
  );
  return protocol.post(EVERGREEN_API.CONTACT_US, payload);
};

const CONFIG_API = "/config";
export const getConfig = () => {
  return protocol.get(CONFIG_API);
};

const BANK_API = "/listOfBanks";
export const getBanks = () => {
  return protocol.get(BANK_API);
};

// SHIELD STARTER API CALL START
export const getBaseURLNew = () => {

  if (process.env.REACT_APP_ENVIRONMENT === "DEV") {
    const host =
    window.location.protocol +
    "//" +
    window.location.host +
    window.location.pathname.replace("index.html", "").slice(0, -1);

    return host;
  }


  const configURLInHtml = get(window, "AVIVA_VARIABLE_CONFIG.urlApi", null);
  const host =
    window.location.protocol +
    "//" +
    window.location.host +
    window.location.pathname.replace("index.html", "").slice(0, -1); //+'/api/v1/directsales/';

  
  return configURLInHtml ? configURLInHtml : host;
};

const configNew: AxiosRequestConfig = {
  baseURL: getBaseURLNew(),
  headers: {
    "Access-Control-Allow-Origin": "*",
    "Content-Type": "application/json",
    "X-XSRF-TOKEN": "",
  },
};

export const protocolNew = axios.create(configNew);

const configUpload: AxiosRequestConfig = {
  baseURL: getBaseURLNew(),
  headers: {
    "Access-Control-Allow-Origin": "*",
    "Content-Type": "multipart/form-data",
    "X-XSRF-TOKEN": window.sessionStorage.getItem("token") || "",
  },
};

export const protocolUpload = axios.create(configUpload);

const CSRF = "/security/csrf-token";
const VALIDATE_DATE_URL = "/shield-starter/get-anb-validation-date";
const QUOTATION_VIEW_URL = "/shield-starter/quotation-view";
const MY_INFO_URL = "/shield-starter/myinfo/url";
const PERSONAL_MY_INFO_PAGE_URL = "/shield-starter/myinfo/person-record";
const ADDRESS_URL = "/shield-starter/address";
const BMI_CALCULATION_URL = "/shield-starter/bmi";
const GET_UW_QUESTION_URL = "/shield-starter/get-uw-question";
const VALIDATE_ADVISER = "/shield-starter/verify-agent-code";
const CONTACT_SHIELD_API = "/shield-starter/add-drop-off-journey";
const MANUAL_SINGPASS_API = "/shield-starter/populate-personal-detail-manually";
const GET_PAYMENT_URL = "/shield-starter/payment-url";
const SAVE_PLAN_URL = "/shield-starter/confirmation";
const GET_POLICY_NUMBERS = "/shield-starter/get-policy-numbers";
const GET_INSURER_LIST = "/shield-starter/insurer-list";
const POST_VALIDATE_USER_DETAILS =
  "/shield-starter/myinfo/validate-user-detail";
const POST_SIGNATURE_DATA = "/shield-starter/store-signature";
const POST_DOCUMENT_DATA = "/shield-starter/store-attachment";
const DELETE_DOCUMENT_DATA = "/shield-starter/delete-attachment";
const GET_CAMPAIGN_DATA = "/shield-starter/campaign";
const POST_EVERGREEN_ADVISER = "/ever-green/verify-agent-code";
const POST_CANCER_COVER_ADVISER = "/ccp/verify-agent-code";

export const DOWNLOAD_DOCUMENT_DATA = "/shield-starter/download-attachment";

const GIO_CAMPAIGN_ELIGIBILITY = "/campaignEligibility";
export const GIOCampaignEligibilityCheck = (payload: any) => {
  return protocolNew.post(GIO_CAMPAIGN_ELIGIBILITY, payload);
};

const QR_CLIENT_DETAILS = "/mltc/processQrClientNum";
export const QRClientDetails = (payload: any) => {
  return protocolNew.post(QR_CLIENT_DETAILS, payload);
};

export const postValidatePromoCode = (
  code: string,
  selectedPlan: string,
  whomToBuy: string,
  campaignCode?: string
) => {
  const body = generateSqsQuotationRequest(
    whomToBuy,
    selectedPlan,
    code,
    undefined,
    campaignCode
  );
  return protocolNew.post(QUOTATION_VIEW_URL, body);
};

export const getCsrfToken = () => {
  return protocolNew.get(CSRF);
};

export const postValidateAdviser = (
  code: string,
  productCode: PRODUCT_CODE_API,
  otherParams?: Partial<{
    agentCode: string;
    promoCode: string;
    clientNo: string;
    loggedInUserId: number;
    loginUser: string;
    requesterClientNumber: string;
    sourceSystem: string;
    agentSelectedType: string;
    customerId: number | string | null;
  }>
) => {
  let url = VALIDATE_ADVISER;
  let body: Record<string, string | number | null> = {
    agentCode: code,
    promoCode: '',
    clientNo: "",
    loggedInUserId: 0,
    loginUser: "",
    requesterClientNumber: "",
    sourceSystem: "",
    agentSelectedType: "",
  };
  if (!otherParams) {
    otherParams = {};
  }
  switch (productCode) {
    case PRODUCT_CODE_API.EVER_GREEN: {
      body.sourceSystem = "evergreen";
      body = {
        ...body,
        ...otherParams,
      };
      url = POST_EVERGREEN_ADVISER;
      break;
    }
    case PRODUCT_CODE_API.CANCER_COVER: {
      body = otherParams;
      url = POST_CANCER_COVER_ADVISER;
      break;
    }
    case PRODUCT_CODE_API.SHIELD_STARTER:
    default:
      body.sourceSystem = "shieldStater";
      url = VALIDATE_ADVISER;
  }

  return protocolNew.post(url, body);
};

// const QUOTAION_VIEW = '/shieldStarter/quotationView'
export const postQuotationView = (
  whomToBuy: string,
  selectedPlan: string,
  promoCode?: string,
  singpassUserObject?: Object,
  campaignCode?: string
) => {
  const body = generateSqsQuotationRequest(
    whomToBuy,
    selectedPlan,
    promoCode,
    singpassUserObject,
    campaignCode
  );
  return protocolNew.post(QUOTATION_VIEW_URL, body);
};
// const MY_INFO = '/shield-starter/myinfo/person-record'
export const postMyInfoRecord = (
  transaction_id: string,
  whomToBuy: string,
  selectedPolicy: string,
  promoCode?: string
) => {
  const url = window.location.href.replace("/./", "/");
  const [origin, params] = url.split("?");
  const productCode =
    selectedPolicy === SHIELD_STARTER_HELTH_PLUS ? "NP5C" : "MSI5";
  // const quotationRequest = generateSqsQuotationRequest(whomToBuy,selectedPolicy,promoCode)
  const myInfoReq = {
    transaction_id: transaction_id,
    app_callback: `${origin}?${params.toString()}`,
    productCode: productCode,
    personType: whomToBuy,
    // quotationRequest: JSON.stringify(quotationRequest)
  };

  return protocolNew.post(PERSONAL_MY_INFO_PAGE_URL, myInfoReq);
};

// const UWQ = '/shield-starter/get-uw-question'
export const postUWQuestionRequest = (age: number) => {
  const reqBody = {
    productCode: "SHIELD_STARTER",
    age: age,
  };
  return protocolNew.post(GET_UW_QUESTION_URL, reqBody);
};

// const VALIDATION_DATE_API = '/shield-starter/getAnbValidationDate';
export const getAndValidationDate = () => {
  return protocolNew.get(VALIDATE_DATE_URL);
};

// const GET_ADDRESS_WITH_POSTAL_CODE = "/shield-starter/address"
export const getAddressWithPostalCode = (
  payload: QASPostalCodeRequest,
  overrideUrl?: string
) => {
  return protocolNew.post(overrideUrl || ADDRESS_URL, payload);
};

// const SINGPASS_URL_API1 = '/shield-starter/myinfo/url';
export const getAuthoriseURL1 = (
  productCode: string,
  whomToBuy?: string,
  overrideUrl?: string,
  transaction_id?: string,
  payloadOthers: any = {}
) => {
  const url = window.location.href.replace("/./", "/");
  const [origin, params] = url.split("?");
  const searchParams = new URLSearchParams(params);
  searchParams.delete("status");
  searchParams.delete("transaction_id");
  console.debug("getAuthoriseURL", { origin, params: searchParams.toString() });
  let payload: any;

  if (productCode === "mltc") {
    payload = new MyInfoUrlRequest("mltc");
    payload = { ...payload, productType: "mltc" };
  } else if (productCode === "shieldStarter") {
    payload = new MyInfoUrlRequest(productCode);
    payload = {
      ...payload,
      productCode: "SHIELD_STARTER",
      personType: whomToBuy,
    };
  } else if (productCode === "evergreen") {
    payload = new MyInfoUrlRequest("EVER_GREEN");
    payload = { ...payload, productCode: "EVER_GREEN", transaction_id };
  } else if (productCode === "ccp") {
    payload = {
      ...payloadOthers,
      productCode: "HC2",
      transaction_id,
      personType: whomToBuy,
    };
  } else {
    payload = new MyInfoUrlRequest(productCode);
    payload = { ...payload, productType: productCode };
  }

  return protocolNew.post(overrideUrl || MY_INFO_URL, payload);
};

export const postContactShield = (payload: any) => {
  return protocolNew.post(CONTACT_SHIELD_API, payload);
};

export const postManualSingpass = (payload: any, overrideUrl?: string) => {
  return protocolNew.post(`${overrideUrl || MANUAL_SINGPASS_API}`, payload);
};

// Sheild Starter and CCP Payment Api
export const getPaymentUrl = (
  payload: any,
  productCode?: "CCP" | "SHIELD_STARTER"
) => {
  let url = GET_PAYMENT_URL;

  switch (productCode) {
    case "CCP": {
      url = "/ccp/payment";
      break;
    }
    case "SHIELD_STARTER":
    default: {
      url = GET_PAYMENT_URL;
    }
  }

  return protocolNew.post(url, payload);
};

export const savePlanData = (payload: any) => {
  return protocolNew.post(SAVE_PLAN_URL, payload);
};

export const postBMICheckShield = (payload: any) => {
  return protocolNew.post(BMI_CALCULATION_URL, payload);
};

export const getCampaignData = (payload: any) => {
  return protocolNew.post(GET_CAMPAIGN_DATA, payload);
};

export const checkPaymentSuccess = (queries: any) => {
  return protocolNew.get(`${GET_POLICY_NUMBERS}?${queries}`);
};

export const getInsurerList = () => {
  return protocolNew.get(GET_INSURER_LIST);
};

export const postValidateUserDetails = (payload: any) => {
  return protocolNew.post(POST_VALIDATE_USER_DETAILS, payload);
};

export const postSignatureImage = (payload: string) => {
  return protocolNew.post(POST_SIGNATURE_DATA, payload);
};

export const uploadDocuments = (payload: any, config: any) => {
  return protocolUpload.post(POST_DOCUMENT_DATA, payload, {
    ...config,
  });
};

export const deleteDocuments = (payload: string) => {
  return protocolNew.post(`${DELETE_DOCUMENT_DATA}/${payload}`);
};

// SHIELD STARTER API CALL END

// Ever-green API calls function

export const getMyInFoRecord = (
  transaction_id: string,
  product: PRODUCT_CODE_API,
  type?: string
) => {
  const url = window.location.href.replace("/./", "/");
  const [origin, params] = url.split("?");
  let apiUri = "";
  let productCode = "";
  switch (product) {
    case PRODUCT_CODE_API.EVER_GREEN:
      apiUri = "/ever-green/myinfo/person-record";
      productCode = "EVER_GREEN";
      break;
    case PRODUCT_CODE_API.CANCER_COVER:
      apiUri = "/ccp/myinfo/person-record";
      productCode = "HC2";
      break;
    default:
      break;
  }
  const myInfoReq = {
    transaction_id: transaction_id,
    app_callback: `${origin}?${params.toString()}`,
    productCode: productCode,
    ...(type && { personType: type === "mychild" ? "child" : type }),
  };

  return protocolNew.post(apiUri, myInfoReq);
};
export interface IMyinfoValidation {
  transactionId: string | null | undefined;
  customerId: number | null | undefined;
  email: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  mobileNumber: string | null | undefined;
  customerConsent: string | null | undefined;
  salutation: string | null | undefined;
  insuredType: string | null | undefined;
  agentCode: string | null | undefined;
  campaignCode: string | null;
}
const MY_INFO_VALIDATION = "/ever-green/validate-user-detail";
export const postMyInfoValidation = (payload: IMyinfoValidation) => {
  return protocolNew.post(MY_INFO_VALIDATION, payload);
};

const NOB_URL = "/common/master-data/natureOfBusiness";
export const getNatureOfBusiness = () => {
  return protocolNew.get(NOB_URL);
};

//Save all the user info in BE.
const SAVE_USER_INFO = "/ever-green/declaration";
export const postUserInfoSave = (payload: ISaveInfoPayload) => {
  return protocolNew.post(SAVE_USER_INFO, payload);
};

//Get all dropdown list for evergreen
const DROP_DOWN_EVERGREEN = "/common/master-data/SPE";
export const getDropDownEvergreen = () => {
  return protocolNew.get(DROP_DOWN_EVERGREEN);
};

export interface IEvergreenSQSQuotation {
  premiumAmount: number;
  customerId: number;
  transactionId: string;
}
//get quotation
const GET_SQS_QUOTATION = "/ever-green/quotation-view";
export const postEvergreenQuotation = (
  payload: IEvergreenSQSQuotation,
  cancelToken: CancelTokenSource
) => {
  return protocolNew.post(GET_SQS_QUOTATION, payload, {
    cancelToken: cancelToken.token,
  });
};

const GET_TRENCHE_LIMIT = "/ever-green/get-tranche-limit";
export const getThrencheLimit = (
  amount: number | null = null,
  cancelToken: CancelTokenSource | undefined
) => {
  return protocolNew.post(
    GET_TRENCHE_LIMIT,
    {
      premiumAmt: amount,
    },
    { cancelToken: cancelToken?.token }
  );
};

const POST_PAYMENT = "/ever-green/confirm-payment";
export const postPaymentEvergreen = (payload: IEvergreenPaymentPayload) => {
  return protocolNew.post(POST_PAYMENT, payload);
};

// Ever-green api calls function ends

const GET_FAST_BANK_LIST_URL = "/collection/bank-list";
export const getBankListURL = () => {
  return protocolNew.get(GET_FAST_BANK_LIST_URL);
};

// TO initiate the consent by redirecting to the bank
const GET_FAST_BANK_URL = "/collection/fast-register";
export const getFastBankUrl = (payload: IFastBankUrlPayload) => {
  return protocolNew.post(GET_FAST_BANK_URL, payload);
};

// To inititate the payment
const POST_FAST_BANK_PAYMENT_URL = "/collection/edda-fast-transaction";
export const postFastPayment = (
  payload: IFastPaymentPayload,
  cancelToken: CancelTokenSource
) => {
  return protocolNew.post(POST_FAST_BANK_PAYMENT_URL, payload, {
    cancelToken: cancelToken?.token,
  });
};

//Check the Fast payment status manually
const GET_CHECK_FAST_STATUS = "/collection/fast-enquiry";
export const getFastPaymentStatus = (payload: {
  collectionReferenceId: string;
}) => {
  return protocolNew.post(GET_CHECK_FAST_STATUS, payload);
};

//Get Campaign code details
const POST_CAMPAIGN_CODE = "/ever-green/campaign";
export const getEverGreenCampaignCode = (payload: { campaign: string }) => {
  return protocolNew.post(POST_CAMPAIGN_CODE, payload);
};

const QR_GENERATE = "/collection/paynow/generate-qr-code";
export const postQrEvergreen = (payload: {
  transactionId: string;
  trackingCodeJson: any,
  productName: string;
  customerId: number;
}) => {
  console.debug("postQrEvergreen/payload:", JSON.stringify(payload, null, 2));
  return protocolNew.post(QR_GENERATE, payload);
};

const PAYNOW_STATUS = "/collection/paynow/transaction-enquiry";
export const postPaynowEvergreen = (payload: {
  collectionTxnRefNo: string;
  transactionId: string;
  requestReferenceNumber: string;
  productName: string;
  customerId: number;
}) => {
  console.debug(
    "postPaynowEvergreen/payload:",
    JSON.stringify(payload, null, 2)
  );
  return protocolNew.post(PAYNOW_STATUS, payload);
};

////// Cancer cover plus
const GET_ACCEPTED_DATES = "/ccp/get-anb-validation-date";
export const getAcceptedDates = () => {
  return protocolNew.get(GET_ACCEPTED_DATES);
};

const POST_QUOTATION_URL = "/ccp/quotation-view";
export const postQuotationDataCCP = ({
  insuredType,
  paymentFrequency,
  dob,
  agentCode,
  promoCode,
  customerId,
  transactionId,
}: {
  insuredType: string;
  paymentFrequency: string;
  dob: string;
  agentCode: string;
  promoCode: string;
  customerId: string | 0;
  transactionId: string;
}) => {
  return protocolNew.post(POST_QUOTATION_URL, {
    insuredType,
    paymentFrequency,
    dob,
    agentCode,
    promoCode,
    customerId,
    transactionId,
  });
};

const POST_CONTACT_URL = "/ccp/add-drop-off-journey";
export const postContactCCP = (payload: ContactInfo) => {
  console.debug("postContactCCP/payload:", JSON.stringify(payload, null, 2));
  return protocolNew.post(POST_CONTACT_URL, payload);
};

export interface IMyinfoUpdateCCP {
  transactionId: string | null | undefined;
  customerId: number | null | undefined;
  email: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  mobileNumber: string | null | undefined;
  customerConsent: string | null | undefined;
  salutation: string | null | undefined;
  insuredType: "myself" | "child";
  childData?: {
    birthCertNo: string;
    lastName: string;
    firstName: string;
    salutation: string;
  };
  underwritingQuestions?: {
    health?: {
      questionId: string;
      answer: string;
    }[];
  };
}
const POST_MY_INFO_UPDATE = "/ccp/update-customer-detail";
export const postMyInfoUpdateCCP = (payload: IMyinfoUpdateCCP) => {
  return protocolNew.post(POST_MY_INFO_UPDATE, payload);
};
export const getPolicyNumberCCP = (referenceNumber: string) => {
  return protocolNew.get(
    `/ccp/get-policy-numbers?referenceNumber=${referenceNumber}`
  );
};
export const CCP_API_ADDRESS = "/ccp/address";

export default axios;
